import { useEffect, useState } from 'react';
import {
	TextField,
	Box,
	Stack,
	IconButton,
	useTheme,
	Pagination,
	Typography,
	CircularProgress,
} from '@mui/material';
import Button from '../../../../../components/Button/Button';
import '../../EmailContent/EmailContent.scss';
import FileExport from '../../../../../assets/Images/FileExport.svg';
import Copy from '../../../../../assets/Images/Copy.svg';
import Share from '../../../../../assets/Images/Share.svg';
import './EmailBody.scss';
import Input from '../../../../../components/Input/Input';
import { useAIEmailContext } from '../../AIEmailContext';
import useApiService from '../../../../../services/api.service';
import AIEmailGeneratorEndpoints from '../../AIEmailGenerator.endpoints';
import {
	hideLoader,
	showLoader,
} from '../../../../../components/CustomLoader/CustomLoader';
import useToast from '../../../../../components/Toast/hooks/useToast';
import { AppUtilityFunctions } from '../../../../../utils/appUtils';
import FilterIcon from '../../../../../assets/Images/filterIconSvg.svg';
import FilterModal from './FilterModal/FilterModal';
import ToolTipOnText from '../../../../../components/Tooltip/Tooltip';

const EmailBody = (props) => {
	const theme = useTheme();
	const toaster = useToast();
	const [page, setPage] = useState(1);
	const [companyName, setCompanyName] = useState<string>('');
	const [contactName, setContactName] = useState<string>('');
	const [subject, setSubject] = useState('');
	const [filterModal, setFilterModal] = useState(false);
	const [body, setBody] = useState('');
	const APIService = useApiService();

	const {
		emailData,
		setEmailData,
		loader,
		emailNameOptions,
		setSuggestedEmailName,
		setEmailNameOptions,
	} = useAIEmailContext();

	const isLightTheme = theme.palette.mode === 'light';
	const showCompanyNameDropdown =
		props.selectedValue === 2 || props.selectedValue === 3;
	const showContactNameDropdown = props.selectedValue === 3;
	const showFilterIcon = props.selectedValue === 2 || props.selectedValue === 3;

	const icons = [Copy, FileExport, Share];

	const emailMsgList = emailData?.data?.email_msg || [];
	const currentEmail = emailMsgList[page - 1];

	useEffect(() => {
		return () => {
			setContactName('');
			setCompanyName('');
		};
	}, [props.selectedValue, props.segment, props.emailName]);

	useEffect(() => {
		if (!currentEmail?.email_msg) {
			setSubject('');
			setBody('');
			return;
		}

		if (typeof currentEmail.email_msg === 'object') {
			const msg = currentEmail.email_msg;

			const parsedSubject = msg.subject || '';
			// Joining all paragraphs together for display
			const parsedBody = [
				msg.salutation,
				msg.P1,
				msg.P2,
				msg.P3,
				msg.P4,
				msg.closing,
			]
				.filter(Boolean) // remove null/undefined
				.join('\n\n');

			setSubject(parsedSubject);
			setBody(parsedBody);
		} else {
			// fallback to old string parsing (if API sometimes returns old format)
			const lines = currentEmail.email_msg.split('\n');
			let parsedSubject = '';
			let bodyStartIndex = 0;

			for (let i = 0; i < lines.length; i++) {
				if (lines[i].startsWith('Subject:')) {
					parsedSubject = lines[i].replace('Subject:', '').trim();
					bodyStartIndex = i + 1;
					break;
				}
			}

			const parsedBody = lines.slice(bodyStartIndex).join('\n').trim();

			setSubject(parsedSubject);
			setBody(parsedBody);
		}
	}, [page, emailData]);


	const updateEmailContent = (newSubject: string, newBody: string) => {
		if (!emailData) return;

		const updatedEmailData = { ...emailData };
		const emailMsgs = [...updatedEmailData.data.email_msg];

		if (emailMsgs[page - 1]) {
			if (typeof emailMsgs[page - 1].email_msg === 'object') {
				const msg = { ...emailMsgs[page - 1].email_msg };

				// Split body by blank lines
				const parts = newBody
					.split(/\n\s*\n/)
					.map((p) => p.trim())
					.filter(Boolean);

				let salutation = msg.salutation || '';
				let closing = msg.closing || '';
				let paragraphs: string[] = [];

				//  salutation
				if (parts.length > 0 && /^hello/i.test(parts[0])) {
					salutation = parts[0];
					parts.shift();
				}

				//  closing
				if (
					parts.length > 0 &&
					/^(best regards|thanks|sincerely|regards)/i.test(
						parts[parts.length - 1]
					)
				) {
					closing = parts[parts.length - 1];
					parts.pop();
				}

				paragraphs = parts;

				// Rebuild email_msg cleanly
				const newMsg = {
					...msg,
					subject: newSubject,
					salutation,
					closing,
				};

				paragraphs.forEach((p, idx) => {
					newMsg[`P${idx + 1}`] = p;
				});

				// Removing old unused paragraphs (avoid duplication)
				Object.keys(newMsg).forEach((key) => {
					if (/^P\d+$/.test(key) && Number(key.slice(1)) > paragraphs.length) {
						delete newMsg[key];
					}
				});

				emailMsgs[page - 1] = {
					...emailMsgs[page - 1],
					email_msg: newMsg,
				};
			} else {
				// old string format fallback
				const updatedMsg = `Subject: ${newSubject}\n${newBody}`;
				emailMsgs[page - 1] = {
					...emailMsgs[page - 1],
					email_msg: updatedMsg,
				};
			}

			updatedEmailData.data.email_msg = emailMsgs;
			setEmailData(updatedEmailData);
		}
	};

	const handleSubjectChange = (e) => {
		const newSubject = e.target.value;
		setSubject(newSubject);
		updateEmailContent(newSubject, body);
	};

	const handleBodyChange = (e) => {
		const newBody = e.target.value;
		setBody(newBody);
		updateEmailContent(subject, newBody);
	};

	const handlePageChange = (_: any, value: number) => {
		setPage(value);
	};

	const onSaveChanges = async () => {
		if (!emailData?.data?.email_msg?.length) {
			toaster.addToast({
				message: 'Nothing to Save',
				timeout: 2000,
				type: 'error',
			});
			return;
		}

		const request = {
			...emailData,
		};
		showLoader();
		try {
			await APIService.post(
				AIEmailGeneratorEndpoints.storeEmailLogs(request),
				true
			);
			hideLoader();
			emailNameOptions.push(emailData.data.email_name);
			setEmailNameOptions(emailNameOptions);
			toaster.addToast({
				message: 'Email campaign saved successfully',
				timeout: 2000,
				type: 'success',
			});
			const currentName = emailData?.data?.email_name;
			const nextName = AppUtilityFunctions.getNextEmailName(currentName);
			setSuggestedEmailName(nextName);
		} catch (err) {
			console.error(err);
		} finally {
			hideLoader();
		}
	};

	return (
		<div className='component-container'>
			<Box className='email-template'>
				<Typography fontSize='18px' fontWeight={600}>
					Email Template Preview Here
				</Typography>

				<Box display='flex' justifyContent='space-between' alignItems='center'>
					<Typography fontSize='15px' fontWeight={400} mt={3} mb={2}>
						Describe your Email
					</Typography>
					<Box gap={1} display='flex'>
						{showFilterIcon && (
							<ToolTipOnText title='Filter Emails'>
								<IconButton onClick={() => setFilterModal(true)}>
									<img src={FilterIcon} alt='filter' height={25} width={25} />
								</IconButton>
							</ToolTipOnText>
						)}
					</Box>
				</Box>

				<Input
					name='email-title'
					placeholder='Your Email Title'
					className='email-title'
					value={subject}
					onChange={handleSubjectChange}
					sx={{
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: isLightTheme
								? 'rgba(0, 0, 0, 0.23)'
								: 'rgba(255, 255, 255, 0.23)',
							borderWidth: '1px',
						},
					}}
				/>

				<Typography fontSize='15px' fontWeight={400} mt={3} mb={1}>
					Email Body
				</Typography>

				<Box
					className={`gradient-border-box ${isLightTheme ? 'light' : 'dark'}`}
				>
					<TextField
						variant='outlined'
						fullWidth
						value={body}
						onChange={handleBodyChange}
						placeholder='Write Email Here ...'
						multiline
						maxRows={20}
						minRows={20}
						className={`gradient-textfield ${isLightTheme ? 'light' : 'dark'}`}
					/>
					<Box mr={1} display='flex' justifyContent='end'>
						{emailMsgList.length > 0 && (
							<Pagination
								showFirstButton
								showLastButton
								count={emailMsgList.length}
								page={page}
								siblingCount={0}
								onChange={handlePageChange}
								sx={{
									'& .MuiPaginationItem-root.Mui-selected': {
										backgroundImage: isLightTheme
											? `linear-gradient(white, white), linear-gradient(90deg, #9149ff, #e600ff)`
											: `linear-gradient(black, black), linear-gradient(90deg, #9149ff, #e600ff)`,
										color: theme.palette.text.primary,
									},
								}}
								className={`custom-pagination ${
									isLightTheme ? 'light' : 'dark'
								}`}
							/>
						)}
					</Box>
				</Box>

				<Box className='footer-container'>
					<Stack direction='row' alignItems='center' mt={2}>
						<Stack direction='row' spacing={0.4} ml={1}>
							{icons.map((icon, index) => (
								<IconButton className='icon-button' key={index}>
									<img src={icon} alt='icon' />
								</IconButton>
							))}
						</Stack>
					</Stack>
					<Button
						type='btn'
						btnText='Save All'
						className='approve-btn'
						onClick={() => onSaveChanges()}
					/>
				</Box>
			</Box>
			{loader && (
				<div className='loader-overlay'>
					<CircularProgress />
				</div>
			)}
			{filterModal && (
				<FilterModal
					handleClose={() => setFilterModal(false)}
					emailData={emailData}
					contactName={contactName}
					setContactName={setContactName}
					showCompanyNameDropdown={showCompanyNameDropdown}
					showContactNameDropdown={showContactNameDropdown}
					companyName={companyName}
					setCompanyName={setCompanyName}
				/>
			)}
		</div>
	);
};

export default EmailBody;
